import { getDiscordWsTicket } from '@bn-games/auth-client';

import { isDiscordActivity } from './guard.js';
import { loadDiscordSdk } from './sdkLoader.js';
import { buildUrlMappings } from './urlMappings.js';

export interface DiscordActivityContext {
  /** Discord's per-launch instance id — the room key everyone in the call shares. */
  instanceId: string;
  /**
   * HS256 ticket the game server already understands, or null when the auth
   * Worker could not mint one. Null means the seat is anonymous, not that the
   * activity is broken — see `runHandshake`.
   */
  ticket: string | null;
  /** The BN-games account id the ticket's `sub` is signed with — not the Discord snowflake. */
  userId: string | null;
  displayName: string;
  avatarUrl: string | null;
}

/** Shown when Discord never told us who the player is. */
const ANONYMOUS_DISPLAY_NAME = 'Player';

interface DiscordUserLike {
  id: string;
  username: string;
  global_name?: string | null;
  avatar?: string | null;
}

const AVATAR_CDN_BASE = 'https://cdn.discordapp.com/avatars';
const AVATAR_EXTENSION = '.png';
const LOG_PREFIX = '[discord]';

let handshake: Promise<DiscordActivityContext | null> | null = null;

/**
 * Runs the Discord embedded-app handshake once per page load. Resolves to null
 * outside Discord and on every failure path — a broken activity must degrade to
 * the normal menu instead of white-screening app startup.
 */
export function initDiscordActivity(): Promise<DiscordActivityContext | null> {
  if (handshake === null) {
    handshake = runHandshake();
  }
  return handshake;
}

async function runHandshake(): Promise<DiscordActivityContext | null> {
  if (!isDiscordActivity()) {
    return null;
  }

  const clientId = readClientId();
  if (clientId === null) {
    console.error(LOG_PREFIX, 'VITE_DISCORD_CLIENT_ID is not set — activity disabled');
    return null;
  }

  try {
    const { DiscordSDK, patchUrlMappings } = await loadDiscordSdk();
    const sdk = new DiscordSDK(clientId);
    await sdk.ready();
    patchUrlMappings(buildUrlMappings());

    const { code } = await sdk.commands.authorize({
      client_id: clientId,
      response_type: 'code',
      state: '',
      prompt: 'none',
      scope: ['identify'],
    });

    const granted = await getDiscordWsTicket(code, clientId);
    if (granted === null) {
      // The auth Worker is unreachable or misconfigured. That costs us the
      // player's identity, but not the activity: the instance id still keys the
      // room, and the server already seats ticketless clients anonymously. A
      // dead identity service should degrade the table, not close it.
      //
      // Note this returns without `authenticate`, which needs an access token
      // we never got — so on this path the SDK's RPC session stays
      // unauthenticated. Nothing calls an SDK command after the handshake
      // today. Anything added later (participants roster, setActivity, voice
      // state) must tolerate that, or it will work everywhere except when the
      // Worker is down, which is the path least likely to be tested.
      console.warn(LOG_PREFIX, 'ws-ticket exchange failed — seating anonymously');
      return {
        instanceId: sdk.instanceId,
        ticket: null,
        userId: null,
        displayName: ANONYMOUS_DISPLAY_NAME,
        avatarUrl: null,
      };
    }

    const auth = await sdk.commands.authenticate({ access_token: granted.accessToken });

    return {
      instanceId: sdk.instanceId,
      ticket: granted.ticket,
      userId: granted.accountId,
      ...profileOf(auth.user),
    };
  } catch (error) {
    console.error(LOG_PREFIX, 'handshake failed', error);
    return null;
  }
}

function readClientId(): string | null {
  const clientId = import.meta.env.VITE_DISCORD_CLIENT_ID as string | undefined;
  if (clientId === undefined || clientId === '') {
    return null;
  }
  return clientId;
}

function profileOf(
  user: DiscordUserLike
): Omit<DiscordActivityContext, 'instanceId' | 'ticket' | 'userId'> {
  const globalName = user.global_name;
  const avatar = user.avatar;

  return {
    displayName:
      globalName === undefined || globalName === null || globalName === ''
        ? user.username
        : globalName,
    avatarUrl:
      avatar === undefined || avatar === null
        ? null
        : AVATAR_CDN_BASE + '/' + user.id + '/' + avatar + AVATAR_EXTENSION,
  };
}
